import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Header } from "../Header";
import { Footer } from "../Footer";
import { legalConfig } from "../../data/legalConfig";

export const LegalLayout = ({ title, description, children, testId, breadcrumbPath }) => {
  useEffect(() => {
    window.scrollTo(0, 0);
    const previousTitle = document.title;
    document.title = `${title} — ${legalConfig.brandName}`;

    let metaDesc = document.querySelector('meta[name="description"]');
    const previousDesc = metaDesc?.getAttribute("content");
    if (description) {
      if (!metaDesc) {
        metaDesc = document.createElement("meta");
        metaDesc.setAttribute("name", "description");
        document.head.appendChild(metaDesc);
      }
      metaDesc.setAttribute("content", description);
    }

    return () => {
      document.title = previousTitle;
      if (metaDesc && previousDesc) metaDesc.setAttribute("content", previousDesc);
    };
  }, [title, description]);

  // BreadcrumbList JSON-LD
  const breadcrumb = breadcrumbPath
    ? {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Accueil", item: legalConfig.websiteUrl + "/" },
          {
            "@type": "ListItem",
            position: 2,
            name: title,
            item: legalConfig.websiteUrl + breadcrumbPath,
          },
        ],
      }
    : null;

  return (
    <div className="bg-[#0E1A2B] min-h-screen flex flex-col">
      {breadcrumb && (
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }}
        />
      )}
      <Header />

      <main
        data-testid={testId}
        className="flex-1 pb-16"
        style={{ paddingTop: "calc(8rem + var(--promo-banner-h, 0px))" }}
      >
        <div className="max-w-[820px] mx-auto px-6 md:px-10">
          {/* Breadcrumb visible */}
          <nav className="mb-8 text-xs text-[#F7F3EA]/55 tracking-wider" aria-label="Fil d'Ariane">
            <Link to="/" className="hover:text-[#C9A869] transition-colors">Accueil</Link>
            <span className="mx-2 text-[#C9A869]/40">/</span>
            <span className="text-[#C9A869]">{title}</span>
          </nav>

          {/* Titre */}
          <div className="mb-12">
            <div className="flex items-center gap-3 mb-5">
              <span className="h-px w-10 bg-[#C9A869]" />
              <span className="text-[11px] tracking-[0.4em] uppercase text-[#C9A869]">
                {legalConfig.brandName}
              </span>
            </div>
            <h1 className="serif font-light text-4xl md:text-5xl lg:text-6xl text-[#F7F3EA] tracking-tight leading-[1.05]">
              {title}
            </h1>
            <p className="mt-4 text-xs tracking-[0.25em] uppercase text-[#F7F3EA]/45">
              Dernière mise à jour : {new Date().toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" })}
            </p>
          </div>

          {/* Contenu */}
          <div className="prose-legal text-[#F7F3EA]/85 font-light leading-relaxed">
            {children}
          </div>

          {/* Retour à l'accueil */}
          <div className="mt-16 pt-8 border-t border-[#C9A869]/20 flex justify-center">
            <Link
              to="/"
              data-testid="legal-back-home"
              className="inline-flex items-center gap-3 px-7 py-3 border border-[#C9A869] text-[#C9A869] text-[11px] tracking-[0.25em] uppercase hover:bg-[#C9A869] hover:text-[#0E1A2B] transition-colors duration-500"
            >
              <ArrowLeft size={14} />
              Retour à l'accueil
            </Link>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

// Helpers
export const Section = ({ title, children, testId }) => (
  <section data-testid={testId} className="mb-10">
    {title && (
      <h2 className="serif text-2xl md:text-3xl text-[#F7F3EA] font-light mb-4 mt-2">
        {title}
      </h2>
    )}
    <div className="space-y-3 text-base text-[#F7F3EA]/80 leading-relaxed">
      {children}
    </div>
  </section>
);

export const Field = ({ label, value }) => (
  <div className="flex flex-col sm:flex-row sm:gap-4 py-2 border-b border-[#C9A869]/15">
    <span className="text-[10px] tracking-[0.3em] uppercase text-[#C9A869] sm:w-52 shrink-0 mt-1">
      {label}
    </span>
    <span className="text-[#F7F3EA]/85">{value}</span>
  </div>
);

export const InfoNote = ({ children, variant = "info" }) => {
  const styles = {
    info: "border-[#C9A869]/40 bg-[#C9A869]/5",
    warning: "border-[#C47A66]/50 bg-[#C47A66]/8",
  };
  return (
    <div className={`my-6 border ${styles[variant]} px-5 py-4 text-sm text-[#F7F3EA]/75 font-light leading-relaxed`}>
      {children}
    </div>
  );
};
